/**
 * Who may see which audit records, and how a viewer narrows them.
 *
 * ## The scope rule is the reason this file exists
 *
 * `GET /audit` is `tenantScoped: false`, and correctly so — a record of a denied
 * cross-tenant attempt belongs to the attempt, not to the tenant that was
 * probed, so there is no single tenant in the path to authorize against. But
 * "nothing to authorize" is not "nothing to filter": the rule in this codebase
 * is that a list endpoint with no tenant in its path filters by
 * `visibleTenants`, and until IM-42 the audit read did neither. A
 * `platform-admin` scoped to one customer — a valid assignment, since role and
 * scope are independent — could read every customer's audit trail.
 *
 * ## Why a viewer's own actions are visible regardless
 *
 * A scoped viewer sees events for tenants in their scope, plus events they
 * themselves caused. The second half matters because the interesting untenanted
 * events are refusals — someone denied on `GET /tenants`, or an `access-change`
 * — and a person should be able to see what they themselves were refused. They
 * already know they tried.
 *
 * The converse is deliberate: a scoped viewer does **not** see a colleague's
 * untenanted events. `authorization-decision` records the request `path`, and a
 * path can name a tenant, so showing those would leak the customer list by
 * another route — which is precisely what `visibleTenants` exists to prevent.
 */
/**
 * The most days one request may span.
 *
 * The store is partitioned by day, so a range is one query per day — a bound is
 * the difference between a slow screen and an accidental table read. A month
 * covers "who touched our account" without inviting a year.
 */
export const MAX_AUDIT_DAYS = 31;
/** Whether an event is a refusal, whichever kind it is. */
export const isDenial = (event) => event.kind === 'authorization-decision' && !event.allowed;
/**
 * The tenant an event concerns, when it concerns one.
 *
 * An account setting change always names one, unlike an access change: it is a
 * fact about one customer's cloud account, so a scoped viewer sees changes to
 * their own accounts and nobody else's for free, through `visibleToScope`.
 */
export const tenantOf = (event) => {
    switch (event.kind) {
        case 'authorization-decision':
            return event.tenantId;
        case 'account-setting-change':
            return event.tenantId;
        case 'lifetime-change':
            return event.tenantId;
        case 'tenant-created':
            return event.tenantId;
        case 'destroyed-outside':
            return event.tenantId;
        case 'budget-change':
            // Across Qrvey's own fleet, not about one customer — like an access change.
            return undefined;
        case 'access-change':
            // Access is granted across customers, so there is no one tenant it is
            // about. Unchanged, and the reason is worth keeping in view.
            return undefined;
    }
};
/**
 * Whether this viewer may see this event at all.
 *
 * Separate from `matchesFilter` on purpose: one is a permission and the other is
 * a preference, and collapsing them would let a narrower filter look like a
 * wider permission.
 */
export const visibleToScope = (event, scope, viewerEmail) => {
    if (scope.kind === 'all')
        return true;
    if (event.actorEmail.trim().toLowerCase() === viewerEmail.trim().toLowerCase())
        return true;
    const tenantId = tenantOf(event);
    if (tenantId === undefined)
        return false;
    return scope.tenantIds.includes(tenantId);
};
const includesFold = (haystack, needle) => haystack.toLowerCase().includes(needle.trim().toLowerCase());
/** Whether an event matches what the viewer asked for. */
export const matchesFilter = (event, filter) => {
    if (filter.deniedOnly === true && !isDenial(event))
        return false;
    if (filter.actor !== undefined &&
        filter.actor !== '' &&
        !includesFold(event.actorEmail, filter.actor)) {
        return false;
    }
    if (filter.tenantId !== undefined &&
        filter.tenantId !== '' &&
        tenantOf(event) !== filter.tenantId) {
        return false;
    }
    if (filter.capability !== undefined) {
        if (event.kind !== 'authorization-decision' || event.capability !== filter.capability) {
            return false;
        }
    }
    return true;
};
/**
 * The days a range covers, oldest first, clamped to `MAX_AUDIT_DAYS`.
 *
 * Returns the single day containing `to` when no `from` is given, so the default
 * read is one partition rather than a month of them.
 */
export const daysInRange = (filter, today) => {
    const to = filter.to ?? today;
    const from = filter.from ?? to;
    if (from > to)
        return [];
    const days = [];
    // Iterated as UTC dates rather than by string arithmetic, so month and year
    // boundaries and leap days are the calendar's problem and not ours.
    for (let d = new Date(`${from}T00:00:00Z`); days.length < MAX_AUDIT_DAYS;) {
        const day = d.toISOString().slice(0, 10);
        if (day > to)
            break;
        days.push(day);
        d = new Date(d.getTime() + 86_400_000);
    }
    return days;
};
/** One row of a CSV export. Header first, then a row per event. */
export const AUDIT_EXPORT_HEADER = [
    'at',
    'outcome',
    'actor',
    'role',
    'capability',
    'tenant',
    'method',
    'path',
    'requestId',
    'breakGlass',
];
const csvCell = (value) => 
// Quote when the value could otherwise break a row, and double any quote
// inside it. A comma in a path would silently shift every later column.
/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
const exportRow = (event) => {
    if (event.kind === 'budget-change') {
        const side = (b) => b === null
            ? 'none'
            : `$${String(b.monthlyAmount)} warn ${String(b.warnAtPercent)}% ${b.blocksNewInstances ? 'blocking' : 'report-only'}`;
        return [
            event.at,
            'budget-change',
            event.actorEmail,
            '',
            'budget:manage',
            '',
            '',
            `${side(event.before)} -> ${side(event.after)}`,
            event.requestId,
            '',
        ];
    }
    if (event.kind === 'destroyed-outside') {
        return [
            event.at,
            'destroyed-outside',
            event.actorEmail,
            '',
            'records:manage',
            event.tenantId,
            '',
            `${event.deploymentId} in ${event.accountKey}: ${event.marked ? 'marked' : 'withdrawn'} (scan ${event.scannedAt}: ${event.evidence})`,
            event.requestId,
            '',
        ];
    }
    if (event.kind === 'tenant-created') {
        return [
            event.at,
            'tenant-created',
            event.actorEmail,
            '',
            'tenant:manage',
            event.tenantId,
            '',
            `${event.name} created as ${event.tenantKind}`,
            event.requestId,
            '',
        ];
    }
    if (event.kind === 'lifetime-change') {
        return [
            event.at,
            'lifetime-change',
            event.actorEmail,
            '',
            'records:manage',
            event.tenantId,
            '',
            `${event.deploymentId} in ${event.accountKey}: ${event.before ?? 'none set'} -> ${event.after}`,
            event.requestId,
            '',
        ];
    }
    if (event.kind === 'account-setting-change') {
        return [
            event.at,
            'account-setting-change',
            event.actorEmail,
            '',
            'account:settings',
            event.tenantId,
            '',
            `${event.accountKey} ${event.setting}: ${JSON.stringify(event.before)} -> ${JSON.stringify(event.after)}`,
            event.requestId,
            '',
        ];
    }
    return event.kind === 'authorization-decision'
        ? [
            event.at,
            event.allowed ? 'allowed' : `denied:${event.denialReason ?? 'unknown'}`,
            event.actorEmail,
            event.actorRole ?? '',
            event.capability,
            event.tenantId ?? '',
            event.method,
            event.path,
            event.requestId,
            event.breakGlass === true ? 'yes' : '',
        ]
        : [
            event.at,
            'access-change',
            event.actorEmail,
            '',
            'access:manage',
            '',
            '',
            `${event.subjectEmail}: ${JSON.stringify(event.before)} -> ${JSON.stringify(event.after)}`,
            event.requestId,
            '',
        ];
};
/**
 * The trail as CSV, for the customer or auditor who asks who touched their
 * account.
 *
 * CSV rather than the JSON the API already returns, because the person who asks
 * that question opens it in a spreadsheet. Both kinds of event appear, with the
 * outcome column distinguishing an allow from a refusal and from an access
 * change — the alternative is two files that have to be read together.
 */
export const toAuditCsv = (events) => [
    AUDIT_EXPORT_HEADER.join(','),
    ...events.map((event) => exportRow(event).map(csvCell).join(',')),
].join('\n');
//# sourceMappingURL=viewing.js.map